/**
 * Measurements Page - Main container component
 * Orchestrates all measurements-related components and state
 */

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { useTranslation } from "@/hooks/useTranslation";

// Components
import { MeasurementsFilters } from "./components/MeasurementsFilters";
import { MeasurementsSummary } from "./components/MeasurementsSummary";
import { MeasurementsCharts } from "./components/MeasurementsCharts";
import { MeasurementsTable } from "./components/MeasurementsTable";
import { MeasurementsDialog } from "./components/MeasurementsDialog";
import { MeasurementsPrint } from "./components/MeasurementsPrint";

// Hooks
import { useMeasurementsData } from "./hooks/useMeasurementsData";
import { useMeasurementsPrint } from "./hooks/useMeasurementsPrint";

// Types
import type { Meranie, MeasurementFormData } from "./types";

export default function MeasurementsPage() {
  const { t } = useTranslation();

  // State
  const [selectedKlientId, setSelectedKlientId] = useState<number | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMeranie, setEditingMeranie] = useState<Meranie | null>(null);

  // Custom hooks
  const {
    klienti,
    merania,
    latestMeranie,
    chartData,
    summary,
    isLoading,
    createMeasurement,
    updateMeasurement,
    deleteMeasurement,
  } = useMeasurementsData({ selectedKlientId });

  const {
    printSettings,
    setPrintSettings,
    toggleAllColumns,
    areAllColumnsSelected,
    handlePrint,
  } = useMeasurementsPrint();

  useEffect(() => {
    if (klienti.length === 1 && selectedKlientId !== klienti[0].id) {
      setSelectedKlientId(klienti[0].id);
    }
  }, [klienti, selectedKlientId]);

  // Handlers
  const handleOpenDialog = () => {
    setEditingMeranie(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (meranie: Meranie) => {
    setEditingMeranie(meranie);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (data: MeasurementFormData) => {
    if (editingMeranie) {
      return await updateMeasurement(editingMeranie.id, data);
    } else {
      return await createMeasurement(data);
    }
  };

  const handleDelete = async (id: number) => {
    await deleteMeasurement(id);
  };

  const handlePrintClick = () => {
    if (!selectedKlientId) return;

    const selectedKlient = klienti.find(k => k.id === selectedKlientId);
    if (!selectedKlient) return;

    handlePrint(selectedKlient, merania, summary, chartData);
  };

  return (
    <DashboardLayout>
      <div className="container py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold">{t("measurements:title")}</h1>
            <p className="text-muted-foreground">
              {t("measurements:subtitle")}
            </p>
          </div>
          <div className="flex gap-2">
            {selectedKlientId && (
              <>
                <MeasurementsPrint
                  printSettings={printSettings}
                  onSettingsChange={setPrintSettings}
                  onToggleAllColumns={toggleAllColumns}
                  areAllColumnsSelected={areAllColumnsSelected()}
                  onPrint={handlePrintClick}
                />
                <Button onClick={handleOpenDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t("measurements:addMeasurement")}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <MeasurementsFilters
          selectedKlientId={selectedKlientId}
          onKlientChange={setSelectedKlientId}
          klienti={klienti}
        />

        {/* Content - Only shown when client is selected */}
        {selectedKlientId && (
          <>
            {/* Summary Cards */}
            <MeasurementsSummary summary={summary} />

            {/* Progress Charts */}
            <MeasurementsCharts chartData={chartData} />

            {/* Measurements Table */}
            <MeasurementsTable
              merania={merania}
              isLoading={isLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </>
        )}

        {/* Add/Edit Dialog */}
        <MeasurementsDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          editingMeranie={editingMeranie}
          onSubmit={handleSubmit}
          selectedKlientId={selectedKlientId}
        />
      </div>
    </DashboardLayout>
  );
}
